import type { WorksheetSubmissionEntity } from '@packages/domain/techline/worksheet-submissions';
import type { WorksheetFormEntity } from '@packages/domain/worksheets/forms';

import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { getLogger } from '@packages/core/logging';
import { colors } from '@packages/core/styles';
import { noop } from '@packages/core/utils';
import { countInvalidRequiredFields, prepareElementAnswers } from '@packages/domain/techline/worksheet-submissions';
import {
    type DraggableWorksheetFormComponent,
    TechlineStatusCode,
    WorksheetAnswerStatus,
    type WorksheetElementAnswers,
    type WorksheetFormElement as WorksheetFormElementDto,
} from '@packages/models/api';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { WorksheetUploadWarningModal } from '@packages/ui/worksheet/components/worksheet-upload-warning-modal';
import { useWorksheetNavigationGuard } from '@packages/ui/worksheet/hooks/use-worksheet-navigation-guard';
import { SelectPopover } from '@web/administration/views/worksheets/components/selectPopover';
import { WorksheetFormElement } from '@web/administration/views/worksheets/components/worksheet-form-element';
import { WorksheetModal } from '@web/administration/views/worksheets/components/worksheet-modal';
import { WorksheetModal3Button } from '@web/administration/views/worksheets/components/worksheet-modal-3button';
import { prepareFormElementsWithOrder } from '@web/administration/views/worksheets/utils/worksheet-form';
import AsyncPage from '@web/components/async-page';
import Loader from '@web/components/loader';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics/google-analytics.events';
import { analyticsService, googleAnalyticsService } from '@web/services/singletons';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';

import useUserDetailsForWorksheet from '../../../hooks/useUserDetailsOrPermissions';
import { DeleteMessage } from './delete-message.constants';
import { WorksheetToast } from './worksheet-toast';

const logger = getLogger('techline');

/** Options for the onBack callback controlling navigation behavior after leaving the worksheet editor. */
export type WorksheetBackOptions = {
    /** When true, refreshes worksheet groups and submissions before closing the editor. */
    shouldRefresh?: boolean;
};

/**
 * Props for the WorksheetSubmissionSubview component, which renders an inline
 * worksheet editor for filling out and submitting worksheet answers within a
 * techline case.
 */
export type WorksheetSubmissionSubviewProps = {
    /** The case number this worksheet belongs to. */
    caseNumber: string;

    /** The current techline case status, used to determine read-only behavior and submission rules. */
    caseStatus: TechlineStatusCode;

    /** Called when the user navigates back from the worksheet editor to the case view. */
    onBack: (options?: WorksheetBackOptions) => Promise<void>;

    /** Clear the worksheet asset polling timeout (call on unmount). */
    onClearWorksheetAssetPolling: () => void;

    /** Delete a draft worksheet submission by ID. Returns true on success. */
    onDeleteDraftWorksheetSubmission: (submissionId: string) => Promise<boolean>;

    /** Fetch a worksheet form definition by ID (for new submissions). */
    onFetchWorksheetForm: (worksheetId: string) => Promise<WorksheetFormEntity | undefined>;

    /** Fetch an existing worksheet submission by case number and submission ID. */
    onFetchWorksheetSubmission: (caseNumber: string, submissionId: string) => Promise<WorksheetFormEntity | undefined>;

    /** Submit worksheet answers with asset uploads in one operation. Throws on failure. */
    onSubmitWorksheetWithAssets: (params: {
        /** The techline case number. */
        caseNumber: string;

        /** The current case status code. */
        caseStatus: string;

        /** The form element answers keyed by element ID. */
        formElementAnswer: Record<string, WorksheetElementAnswers>;

        /** The target submission status (draft or submitted). */
        status: WorksheetAnswerStatus;

        /** The existing submission ID, or empty string for new submissions. */
        submissionId: string;

        /** The username of the submitting user. */
        userName: string;

        /** The worksheet definition ID. */
        worksheetId: string;
    }) => Promise<WorksheetSubmissionEntity>;

    /** Hide worksheet uploaded files from TechShare by setting displayInTechShare to false. */
    onHideWorksheetFilesFromTechShare: (assetIds: string[]) => Promise<boolean>;

    /** The existing submission ID when editing a previously saved submission. Empty string for new worksheets. */
    submissionId?: string;

    /** The username of the current user, used in submission payloads. */
    userName: string;

    /** The worksheet definition ID to load the form structure from. */
    worksheetId: string;

    /** The worksheet submission data, managed by the parent hook. */
    worksheetSubmissionData: WorksheetFormEntity | undefined;

    /** Whether the worksheet submission data is currently being fetched. */
    worksheetSubmissionFetching: boolean;
};

/** Tracks the delete confirmation modal visibility and the submission ID to delete. */
type DeleteModalState = {
    /** Whether the delete confirmation modal is currently visible. */
    show: boolean;

    /** The submission ID of the draft to delete. */
    submissionId: string;
};

/**
 * Tracks visibility state for action menus, modals, and toasters within the
 * worksheet submission subview.
 */
type Show = {
    /** Whether the action menu popover is visible. */
    actionMenu: boolean;

    /** Whether the draft save confirmation modal is visible. */
    draftModal: boolean;

    /** Whether the submit confirmation modal is visible. */
    submitModal: boolean;

    /** Whether the toast notification is visible. */
    toaster: boolean;

    /** Whether the unsaved changes warning modal is visible. */
    unsavedChangesModal: boolean;
};

/** Background color for the main view area. */
const viewBackgroundStyle = { backgroundColor: colors.grayTwo };

/** Background color for content sections on white backgrounds. */
const whiteBackgroundStyle = { backgroundColor: colors.white };

/** Right margin for the save worksheet button, separating it from the submit button. */
const saveButtonStyle = { marginRight: 5 };

/**
 * Presentational subview for filling out and submitting a worksheet within
 * a techline case. Renders the form elements, handles local form state
 * (answers, validation, modals), and delegates all API interactions to
 * parent callbacks.
 *
 * @param props - component props
 * @returns the worksheet submission subview
 */
export function WorksheetSubmissionSubview({
    caseNumber,
    caseStatus,
    onBack,
    onClearWorksheetAssetPolling,
    onDeleteDraftWorksheetSubmission,
    onFetchWorksheetForm,
    onFetchWorksheetSubmission,
    onHideWorksheetFilesFromTechShare,
    onSubmitWorksheetWithAssets,
    submissionId = '',
    userName,
    worksheetId,
    worksheetSubmissionData,
    worksheetSubmissionFetching,
}: WorksheetSubmissionSubviewProps) {
    // State
    const classes = useWorksheetSubmissionStyles();
    const [formElementsAnswers, setFormElementsAnswers] = useState<Record<string, WorksheetElementAnswers>>({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [invalidFieldCount, setInvalidFieldCount] = useState<number>(0);
    const [isDeletedLoading, setIsDeletedLoading] = useState(false);
    const [isDraftLoading, setIsDraftLoading] = useState(false);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
    const [show, setShow] = useState<Show>({
        actionMenu: false,
        draftModal: false,
        submitModal: false,
        toaster: false,
        unsavedChangesModal: false,
    });
    const [showDeleteModal, setDeleteModal] = useState<DeleteModalState>({
        show: false,
        submissionId: '',
    });
    const [isWorksheetValidationModalVisible, setIsWorksheetValidationModalVisible] = useState(false);
    const [toasterMessage, setToasterMessage] = useState<string>('');

    // Hooks

    // Exception: read-only auth hook qualifies for direct use in a subview (read-only,
    // self-contained lifecycle, no domain state mutation).
    const {
        permissions: { canAddAndEditWorksheet },
    } = useUserDetailsForWorksheet();

    // Navigation guard to warn about uploads in progress
    const {
        hasUploadsInProgress: _hasUploadsInProgress,
        showNavigationModal,
        warningMessage,
        handleConfirmNavigation,
        handleCancelNavigation,
    } = useWorksheetNavigationGuard(worksheetSubmissionData?.elements);

    // Forwarded callback aliases
    const handleClearWorksheetAssetPolling = onClearWorksheetAssetPolling;

    // Derived state

    /**
     * Transforms worksheet entity elements into ordered form elements for rendering.
     * Maps domain entity property names to the DTO property names that prepareFormElementsWithOrder expects.
     */
    // FIXME: Cast needed; prepareFormElementsWithOrder expects WorksheetFormElementDto[] (TECHSHARE-5977)
    //        - Luke C, 03/15/2026
    const elements = useMemo(() => {
        const mappedElements = (worksheetSubmissionData?.elements || []).map((element) => ({
            ...element,
            worksheetElementRequired: element.isRequired,
            worksheetElementAllowMultiple: element.isMultipleSelectionAllowed,
            worksheetElementDefaultValue: element.hasDefaultValue,
            worksheetElementHasRule: element.hasRule,
        }));
        return prepareFormElementsWithOrder(mappedElements as unknown as WorksheetFormElementDto[]);
    }, [worksheetSubmissionData]);

    /** Whether the current worksheet submission has already been submitted (read-only). */
    const isSubmitted = useMemo(() => {
        return worksheetSubmissionData?.worksheetSubmissionStatus === WorksheetAnswerStatus.Submitted;
    }, [worksheetSubmissionData?.worksheetSubmissionStatus]);

    /** Derived booleans for the overall techline case status (closed, submitted, draft). */
    const caseStatusTypes = useMemo(() => {
        return {
            isCloseCase: [
                TechlineStatusCode.CLOSED,
                TechlineStatusCode.AUTO_CLOSED,
                TechlineStatusCode.CLOSE_RESOLVED,
            ].includes(caseStatus),
            isDraftCase: caseStatus === TechlineStatusCode.DRAFT,
            isSubmittedCase: ![
                TechlineStatusCode.DRAFT,
                TechlineStatusCode.CLOSED,
                TechlineStatusCode.CLOSE_RESOLVED,
            ].includes(caseStatus),
        };
    }, [caseStatus]);

    const isWorksheetReadOnly = isSubmitted || caseStatusTypes.isCloseCase || !canAddAndEditWorksheet;

    // Handlers

    /** Toggles visibility of a specific modal or toaster by key. */
    const handleModalAndToasterShow = useCallback(
        (key: keyof Show, action: boolean) => {
            setShow((prev) => ({ ...prev, [key]: action }));
        },
        [setShow]
    );

    /** Updates the answer for a single form element and marks the form as having unsaved changes. */
    const handleAnswerChange = useCallback((newAnswer: WorksheetElementAnswers) => {
        setFormElementsAnswers((prev) => ({ ...prev, [newAnswer.elementId]: newAnswer }));
        setHasUnsavedChanges(true);

        if (newAnswer.asset?.answerFile?.file) {
            setTimeout(() => {
                window.scrollTo({
                    top: document.body.scrollHeight,
                    behavior: 'smooth',
                });
            }, 300);
        }
    }, []);

    /** Handles delete action for worksheet form file assets. */
    const handleDeleteAsset = useCallback(
        async (item: DraggableWorksheetFormComponent) => {
            // Get the current answer to find the asset ID
            const currentAnswer = formElementsAnswers[item.formElementId];
            const assetId = currentAnswer?.asset?.answerFile?.assetId || item.formElementFile?.assetId;

            if (assetId) {
                try {
                    await onHideWorksheetFilesFromTechShare([assetId]);
                } catch (error) {
                    logger.error(`Failed to hide asset from TechShare [${assetId}]`, { assetId, error });
                    alert(`Error hiding asset ${assetId} from TechShare`);
                }
            } else {
                alert('No asset ID found for deletion');
            }
        },
        [formElementsAnswers, onHideWorksheetFilesFromTechShare]
    );

    /**
     * Submits the worksheet answers as the given status via the parent callback. Throws on failure.
     *
     * Analytics live in the callers rather than here: each caller knows which action it is performing, so it can
     * name its event literally instead of deriving the name from `status` at runtime.
     */
    const handleSubmitWithStatus = useCallback(
        async (status: WorksheetAnswerStatus): Promise<WorksheetSubmissionEntity> => {
            return onSubmitWorksheetWithAssets({
                caseNumber,
                caseStatus,
                formElementAnswer: formElementsAnswers,
                status,
                submissionId,
                userName,
                worksheetId,
            });
        },
        [caseNumber, caseStatus, formElementsAnswers, onSubmitWorksheetWithAssets, submissionId, userName, worksheetId]
    );

    /** Submits the worksheet as SUBMITTED, shows a success/error toaster, and navigates back after a delay. */
    const handleSubmitConfirm = useCallback(async () => {
        setIsSubmitLoading(true);
        try {
            await handleSubmitWithStatus(WorksheetAnswerStatus.Submitted);
            googleAnalyticsService.track(GoogleAnalyticsEvents.WorksheetSubmit, { success: true });
            setHasUnsavedChanges(false);
            setToasterMessage('Your worksheet was successfully submitted');
            handleModalAndToasterShow('toaster', true);
            handleModalAndToasterShow('submitModal', false);

            // Show success toaster for 3 seconds, then refresh data before navigating back
            await new Promise<void>((resolve) => setTimeout(resolve, 3000));
            await onBack({ shouldRefresh: true });
        } catch (error) {
            googleAnalyticsService.track(GoogleAnalyticsEvents.WorksheetSubmit, { success: false });
            const message = error instanceof Error ? error.message : 'An unexpected error occurred';
            setToasterMessage(message);
            handleModalAndToasterShow('toaster', true);
            handleModalAndToasterShow('submitModal', false);
        } finally {
            setIsSubmitLoading(false);
        }
    }, [handleSubmitWithStatus, handleModalAndToasterShow, onBack]);

    /** Saves the current answers as a draft and navigates back on success. */
    const handleSaveAsDraft = useCallback(async () => {
        setIsDraftLoading(true);
        try {
            await handleSubmitWithStatus(WorksheetAnswerStatus.Draft);
            googleAnalyticsService.track(GoogleAnalyticsEvents.WorksheetSaveDraft, { success: true });
            setHasUnsavedChanges(false);
            setShow((prev) => ({ ...prev, draftModal: false }));
            await onBack({ shouldRefresh: true });
        } catch (error) {
            googleAnalyticsService.track(GoogleAnalyticsEvents.WorksheetSaveDraft, { success: false });
            logger.error('Failed to save worksheet as draft', { error });
            setShow((prev) => ({ ...prev, draftModal: false }));
        } finally {
            setIsDraftLoading(false);
        }
    }, [handleSubmitWithStatus, onBack]);

    /** Checks for unsaved changes before navigating back; shows a confirmation modal if changes exist. */
    const handleBackNavigation = useCallback(() => {
        if (hasUnsavedChanges) {
            setPendingNavigation('back');
            handleModalAndToasterShow('unsavedChangesModal', true);
        } else {
            onBack();
        }
    }, [hasUnsavedChanges, handleModalAndToasterShow, onBack]);

    /** Handles the three-button unsaved changes modal: exit without saving, cancel, or save and exit. */
    const handleUnsavedChangesModalAction = useCallback(
        async (action: 'left' | 'middle' | 'right') => {
            if (action === 'left' && pendingNavigation) {
                // Exit Without Saving
                setHasUnsavedChanges(false);
                setPendingNavigation(null);
                handleModalAndToasterShow('unsavedChangesModal', false);
                onBack();
            } else if (action === 'right' && pendingNavigation) {
                // Save & Exit
                await handleSaveAsDraft();
                handleModalAndToasterShow('unsavedChangesModal', false);
            } else if (action === 'middle') {
                // Cancel
                setPendingNavigation(null);
                handleModalAndToasterShow('unsavedChangesModal', false);
            }
        },
        [pendingNavigation, handleModalAndToasterShow, handleSaveAsDraft, onBack]
    );

    /**
     * Adjusts the invalid field count when a field's validity changes.
     *
     * @param valid - whether the field is now valid (decrements) or invalid (increments)
     */
    const handleIsValid = useCallback((valid: boolean) => {
        setInvalidFieldCount((prev) => {
            if (valid) {
                return prev - 1;
            }
            return prev + 1;
        });
    }, []);

    /** Saves the worksheet as a draft and navigates back; used by the top-level "Save Worksheet" button. */
    const handleSaveWorksheetButtonClick = useCallback(async () => {
        analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_SAVE_WORKSHEET_CLICKED, {
            worksheetId,
            caseNumber,
            submissionId,
        });
        setIsDraftLoading(true);
        try {
            await handleSubmitWithStatus(WorksheetAnswerStatus.Draft);
            googleAnalyticsService.track(GoogleAnalyticsEvents.WorksheetSaveDraft, { success: true });
            setHasUnsavedChanges(false);
            analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_SAVE_WORKSHEET_SAVED, {
                worksheetId,
                caseNumber,
                submissionId,
            });
            await onBack({ shouldRefresh: true });
        } catch (error) {
            googleAnalyticsService.track(GoogleAnalyticsEvents.WorksheetSaveDraft, { success: false });
            const message = error instanceof Error ? error.message : 'An unexpected error occurred';
            setToasterMessage(message);
            handleModalAndToasterShow('toaster', true);
        } finally {
            setIsDraftLoading(false);
        }
    }, [caseNumber, handleModalAndToasterShow, handleSubmitWithStatus, onBack, submissionId, worksheetId]);

    /** Handles the draft modal confirm/cancel: saves as draft on confirm, closes modal on cancel. */
    const handleDraftModalAction = useCallback(
        async (actions: boolean) => {
            if (actions) {
                await handleSaveAsDraft();
            } else {
                setShow((prev) => ({ ...prev, draftModal: false }));
            }
        },
        [handleSaveAsDraft]
    );

    /** Handles the submit modal confirm/cancel: submits the worksheet on confirm, closes modal regardless. */
    const handleSubmitModalAction = useCallback(
        async (action: boolean) => {
            if (action) {
                await handleSubmitConfirm();
            }
            handleModalAndToasterShow('submitModal', false);
        },
        [handleSubmitConfirm, handleModalAndToasterShow]
    );

    /** Returns the current answer state for a given element by its ID. */
    const getCurrentAnswerByElementId = useCallback(
        (id: string) => {
            return formElementsAnswers[id];
        },
        [formElementsAnswers]
    );

    /** Deletes the draft worksheet submission and shows a success toaster before navigating back. */
    const handleDeleteWorksheetDraft = useCallback(async () => {
        const success = await onDeleteDraftWorksheetSubmission(showDeleteModal.submissionId);
        if (success) {
            setToasterMessage('Worksheet draft deleted successfully');
            handleModalAndToasterShow('toaster', true);
            onBack();
        }
        setIsDeletedLoading(false);
    }, [handleModalAndToasterShow, onBack, onDeleteDraftWorksheetSubmission, showDeleteModal.submissionId]);

    /** Handles the delete modal confirm/cancel: deletes the draft on confirm, closes modal regardless. */
    const handleDeleteModalAction = useCallback(
        async (action: boolean) => {
            if (action) {
                setIsDeletedLoading(true);
                await handleDeleteWorksheetDraft();
            }
            setDeleteModal({ show: false, submissionId: '' });
        },
        [handleDeleteWorksheetDraft]
    );

    /** Hides the worksheet validation modal. Also serves as the modal action handler (confirm just closes). */
    const handleValidationModalHide = useCallback(() => {
        setIsWorksheetValidationModalVisible(false);
    }, []);

    /** Hides the toast notification. */
    const handleToasterClose = useCallback(() => {
        handleModalAndToasterShow('toaster', false);
    }, [handleModalAndToasterShow]);

    /** Hides the unsaved changes modal. */
    const handleUnsavedChangesModalHide = useCallback(() => {
        handleModalAndToasterShow('unsavedChangesModal', false);
    }, [handleModalAndToasterShow]);

    /** Hides the delete modal and resets the submission ID. */
    const handleDeleteModalHide = useCallback(() => {
        setDeleteModal({ show: false, submissionId: '' });
    }, []);

    /** Hides the submit confirmation modal. */
    const handleSubmitModalHide = useCallback(() => {
        handleModalAndToasterShow('submitModal', false);
    }, [handleModalAndToasterShow]);

    /** Hides the draft save confirmation modal. */
    const handleDraftModalHide = useCallback(() => {
        handleModalAndToasterShow('draftModal', false);
    }, [handleModalAndToasterShow]);

    /** Opens the submit confirmation modal for submitting the worksheet as SUBMITTED. */
    const handleSubmitWorksheetButtonClick = useCallback(() => {
        handleModalAndToasterShow('submitModal', true);
    }, [handleModalAndToasterShow]);

    /** Toggles the action menu popover visibility. */
    const handleActionMenuToggle = useCallback(
        (isVisible: boolean) => {
            handleModalAndToasterShow('actionMenu', isVisible);
        },
        [handleModalAndToasterShow]
    );

    /** Logs delete analytics, opens the delete modal, and closes the action menu. */
    const handleDeleteButtonPress = useCallback(() => {
        analyticsService.logEvent(ANALYTICS_EVENTS.USER_DELETES_WORKSHEET_DRAFT);
        setDeleteModal({
            show: true,
            submissionId,
        });
        handleModalAndToasterShow('actionMenu', false);
    }, [handleModalAndToasterShow, submissionId]);

    /** No-op drag end handler; drag reordering is not supported in submission mode. */
    const handleDragEnd = noop;

    /** Fetches worksheet data on mount: submission data if editing, form data if new. */
    const handleFetchData = useCallback(() => {
        if (submissionId) {
            return onFetchWorksheetSubmission(caseNumber, submissionId);
        }
        return onFetchWorksheetForm(worksheetId);
    }, [caseNumber, onFetchWorksheetForm, onFetchWorksheetSubmission, submissionId, worksheetId]);

    // Effects

    /** Initializes form element answers from the worksheet data when it loads or changes. */
    useEffect(() => {
        const elementsSelectedOptions = prepareElementAnswers(worksheetSubmissionData?.elements || []);
        setFormElementsAnswers(elementsSelectedOptions);
    }, [worksheetSubmissionData]);

    /** Counts the number of required fields that are not yet answered, excluding conditional elements. */
    useEffect(() => {
        if (worksheetSubmissionData) {
            setInvalidFieldCount(countInvalidRequiredFields(worksheetSubmissionData));
        }
    }, [worksheetSubmissionData]);

    /** Clears the asset polling timeout when the component unmounts to prevent memory leaks. */
    useEffect(() => {
        return () => {
            handleClearWorksheetAssetPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Ensures that our scroll position is reset upon being opened, has to target the element in charge of scrolling here*/
    useLayoutEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        const scrollContainer = document.querySelector('#routeMainOuterContainer');
        if (scrollContainer instanceof HTMLElement) {
            scrollContainer.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, []);

    return (
        <AsyncPage fetchData={handleFetchData}>
            {worksheetSubmissionFetching ? (
                <Loader />
            ) : (
                <>
                    <WorksheetModal
                        bodyMessage={
                            'Some of the required fields in your worksheets are empty. Please review your worksheets and make sure all of the required fields are complete.'
                        }
                        cancelButton="Cancel"
                        confirmButton="Ok"
                        confirmButtonColor="info"
                        handleAction={handleValidationModalHide}
                        onHideModal={handleValidationModalHide}
                        show={isWorksheetValidationModalVisible}
                        title="Worksheet Validation"
                    />
                    <WorksheetToast body={toasterMessage} onClose={handleToasterClose} show={show.toaster} />
                    <WorksheetModal3Button
                        bodyMessage="If you exit now, any changes you've made since the last save will be lost. What would you like to do?"
                        handleAction={handleUnsavedChangesModalAction}
                        leftButton="Exit without saving"
                        middleButton="Cancel"
                        onHideModal={handleUnsavedChangesModalHide}
                        rightButton="Save & Exit"
                        rightButtonLoading={isDraftLoading}
                        rightButtonVariant="primary"
                        show={show.unsavedChangesModal}
                        title="You have unsaved changes"
                    />
                    <WorksheetModal
                        bodyMessage={DeleteMessage.deleteSubmitted}
                        cancelButton="Cancel"
                        confirmButton="Delete"
                        confirmButtonColor="danger"
                        confirmButtonLoading={isDeletedLoading}
                        handleAction={handleDeleteModalAction}
                        onHideModal={handleDeleteModalHide}
                        show={showDeleteModal.show}
                        title="Delete worksheet"
                    />
                    <WorksheetModal
                        bodyMessage={
                            'By submitting this worksheet, it will become visible to Techline users and can no longer be edited. Are you sure you want continue?'
                        }
                        cancelButton="Cancel"
                        confirmButton="Submit"
                        confirmButtonLoading={isSubmitLoading}
                        handleAction={handleSubmitModalAction}
                        onHideModal={handleSubmitModalHide}
                        show={show.submitModal}
                        title="Submit worksheet"
                    />
                    <WorksheetModal
                        bodyMessage={
                            caseStatusTypes.isSubmittedCase
                                ? 'This worksheet will not be seen by Techline until submitted.'
                                : 'Worksheet will be submitted on case submission.'
                        }
                        cancelButton="Cancel"
                        confirmButton="Save"
                        confirmButtonLoading={isDraftLoading}
                        handleAction={handleDraftModalAction}
                        onHideModal={handleDraftModalHide}
                        show={show.draftModal}
                        title="Save worksheet"
                    />
                    <div className={classes.view} style={viewBackgroundStyle}>
                        <div
                            className="d-flex align-items-center justify-content-between flex-row p-2"
                            style={whiteBackgroundStyle}
                        >
                            <div className="d-flex align-items-center ml-5 flex-row">
                                <Button onPress={handleBackNavigation} variant="link">
                                    <Icon name="arrow-back" />
                                </Button>
                                <div className="ml-4 mr-4">
                                    <Typography variant="h5">{caseNumber}</Typography>
                                </div>
                                <Icon name="chevron-right" />
                                <div className="ml-4">
                                    <Typography color="black" variant="h4">
                                        {worksheetSubmissionData?.displayname}
                                    </Typography>
                                </div>
                            </div>
                            {!caseStatusTypes.isCloseCase && !isSubmitted && (
                                <div className="mr-5">
                                    <SelectPopover
                                        isOpen={show.actionMenu}
                                        options={
                                            <Button
                                                alignTitle="left"
                                                iconLeft={<Icon name="trash" />}
                                                onPress={handleDeleteButtonPress}
                                                title="Delete"
                                                variant="ghost-gray"
                                            />
                                        }
                                        // eslint-disable-next-line no-restricted-syntax -- SelectPopover's API uses `setShow`; not our prop name
                                        setShow={handleActionMenuToggle}
                                    >
                                        <div className="d-flex">
                                            <Button
                                                disabled={isDraftLoading}
                                                isLoading={isDraftLoading}
                                                onPress={handleSaveWorksheetButtonClick}
                                                style={saveButtonStyle}
                                                variant="primary"
                                            >
                                                Save Worksheet
                                            </Button>
                                            {caseStatusTypes.isSubmittedCase && (
                                                <Button
                                                    disabled={Boolean(invalidFieldCount) || isSubmitLoading}
                                                    isLoading={isSubmitLoading}
                                                    onPress={handleSubmitWorksheetButtonClick}
                                                    variant="primary"
                                                >
                                                    Submit Worksheet
                                                </Button>
                                            )}
                                        </div>
                                    </SelectPopover>
                                </div>
                            )}
                        </div>

                        <div className="w-50 m-auto mb-5 mt-5 p-5" style={whiteBackgroundStyle}>
                            {elements.map((element) => (
                                <WorksheetFormElement
                                    getCurrentAnswerByElementId={getCurrentAnswerByElementId}
                                    handleDragEnd={handleDragEnd}
                                    handleIsValid={handleIsValid}
                                    isDraftLoading={isDraftLoading}
                                    isMediaViewOnly={isWorksheetReadOnly}
                                    isSubmitLoading={isSubmitLoading}
                                    item={element}
                                    key={element.formElementId}
                                    onAnswerChange={handleAnswerChange}
                                    onDeleteClick={handleDeleteAsset}
                                    preview
                                    readOnly={isWorksheetReadOnly}
                                />
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Navigation warning modal for uploads in progress */}
            <WorksheetUploadWarningModal
                message={warningMessage}
                onCancel={handleCancelNavigation}
                onConfirm={handleConfirmNavigation}
                visible={showNavigationModal}
            />
        </AsyncPage>
    );
}

const useWorksheetSubmissionStyles = createUseStyles({
    view: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignContent: 'center',
    },
});
